import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  output,
  ViewEncapsulation,
} from '@angular/core';

import { TW_DROPDOWN_CONTEXT } from './tw-dropdown-context';

/**
 * `tw-dropdown-item` — a single menu row (TWS-122 Wave 1).
 *
 * Host is a `<button type="button" role="menuitem">`. Emits
 * `selected` on click and routes a menu-level close request
 * through the shared context so the dropdown dismisses after
 * activation.
 *
 * ### Tabindex strategy
 *
 * We use a simple "all items `tabindex=-1`" approach: the
 * containing menu receives focus programmatically when opened and
 * drives focus movement via `focus()` on the active item. This is
 * a valid WAI-ARIA menu pattern (the alternative — roving
 * tabindex where one item has `tabindex=0` — requires tracking
 * which item is "current" in the menu and rewriting tabindices on
 * every ArrowDown, which is noisier without any behavioural gain
 * for menus that open/close rather than stay persistently
 * focusable).
 *
 * `aria-disabled` (not the HTML `disabled` attribute) is used for
 * the disabled state because WAI-ARIA menus expect disabled
 * items to remain keyboard-focusable; the HTML `disabled`
 * attribute would remove them from the tab sequence, breaking
 * ArrowDown wrap-around.
 */
@Component({
  selector: 'tw-dropdown-item',
  standalone: true,
  template: '<ng-content></ng-content>',
  styleUrls: ['./tw-dropdown-item.component.scss'],
  host: {
    'class': 'tw-dropdown__item',
    'type': 'button',
    'role': 'menuitem',
    'tabindex': '-1',
    '[attr.aria-disabled]': 'disabled() ? "true" : null',
    '(click)': 'onClick($event)',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class TwDropdownItemComponent {
  private readonly ctx = inject(TW_DROPDOWN_CONTEXT);

  /** Disabled items are skipped by ArrowDown/Up and ignore clicks. */
  readonly disabled = input<boolean>(false);

  /**
   * Fires when the user activates the item (click / Enter / Space).
   * Consumers bind `(selected)="doTheThing()"` and don't need to
   * close the dropdown themselves — the item handles that.
   */
  readonly selected = output<void>();

  protected onClick(event: Event): void {
    if (this.disabled()) {
      // Disabled via aria, not HTML — the click event still
      // reaches us. Swallow it so consumers' `(selected)` and
      // the auto-close don't fire.
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    this.selected.emit();
    // Closing via the context returns focus to the trigger.
    this.ctx.requestCloseAndReturnFocus();
  }
}
